import { useState, useEffect, useCallback, type ReactNode } from 'react';
import { authService } from '../services/authService';
import { AuthContext, type User } from './auth';

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(() => authService.getUser());

  const signup = async (data: { firstName: string; lastName: string; email: string; password: string }) => {
    const response = await authService.signup(data);
    if (!response || !response.token) {
      throw new Error('Signup failed: Invalid response from server');
    }
    localStorage.setItem('token', response.token);
    localStorage.setItem('user', JSON.stringify(response.user));
    setUser(response.user);
  };

  const login = async (email: string, password: string) => {
    const response = await authService.login(email, password);
    if (!response || !response.token) {
      throw new Error('Login failed: Invalid response from server');
    }
    localStorage.setItem('token', response.token);
    localStorage.setItem('user', JSON.stringify(response.user));
    setUser(response.user);
  };

  const updateUser = useCallback((data: Partial<User>) => {
    setUser((prevUser) => {
      const updatedUser = { ...(prevUser || {}), ...data } as User;
      localStorage.setItem('user', JSON.stringify(updatedUser));
      return updatedUser;
    });
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const token = authService.getToken();
      if (!token) return;
      
      const profile = await authService.getProfile();
      localStorage.setItem('user', JSON.stringify(profile));
      setUser(profile);
    } catch (error) {
      console.error('Failed to sync user profile:', error);
      // Clean up expired/invalid token to redirect to /login and prevent infinite 403 loops
      authService.logout();
      setUser(null);
    }
  }, []);

  const logout = () => {
    authService.logout();
    setUser(null);
  };

  // Synchronize user state on mount
  useEffect(() => {
    if (authService.getToken()) {
      refreshUser();
    }
  }, [refreshUser]);

  return (
    <AuthContext.Provider value={{ user, isAuthenticated: !!user, login, signup, updateUser, refreshUser, logout }}>
      {children}
    </AuthContext.Provider>
  );
};